/**
 * VH Staff Protected Route - Dedicated auth for restaurant staff
 *
 * Completely independent from demo and OAuth authentication.
 * No shared logic = no cross-contamination.
 *
 * VH Staff are real restaurant employees managing real money.
 * They need bulletproof, zero-glitch authentication.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface VHStaffProtectedRouteProps {
  children: React.ReactNode;
}

const VHStaffProtectedRoute: React.FC<VHStaffProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkVHStaffAuth();
  }, []);

  const checkVHStaffAuth = async () => {
    const userType = localStorage.getItem('user_type');

    // Quick check: if not VH Staff, don't even call the API
    if (userType !== 'vh_staff') {
      console.log('[VHStaffProtectedRoute] Not VH Staff user type');
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      // Validate VH Staff session via backend
      const response = await fetch('/api/vh-staff/auth/me', {
        method: 'GET',
        credentials: 'include'  // Send httpOnly cookies
      });

      if (response.ok) {
        console.log('[VHStaffProtectedRoute] VH Staff authenticated');
        setIsAuthenticated(true);
      } else {
        console.log('[VHStaffProtectedRoute] Auth failed:', response.status);
        setIsAuthenticated(false);

        if (response.status === 401) {
          // Clear all VH Staff localStorage on auth failure
          localStorage.removeItem('user_type');
          localStorage.removeItem('staff_name');
          localStorage.removeItem('restaurant_name');
          localStorage.removeItem('restaurant_slug');
          localStorage.removeItem('restaurant_logo_url');
          localStorage.removeItem('vh_staff_data');
        }
      }
    } catch (error) {
      console.error('[VHStaffProtectedRoute] Auth error:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="h-12 w-12 border-4 border-[#FDB913] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-500">Loading...</p>
        </motion.div>
      </div>
    );
  }

  // Not authenticated - redirect to VH Staff login
  if (!isAuthenticated) {
    const slug = localStorage.getItem('restaurant_slug');
    const redirectTo = slug ? `/staff/${slug}` : '/vh-staff-login';
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Authenticated - render protected content
  return <>{children}</>;
};

export default VHStaffProtectedRoute;
